// Order sets, apply side (PROD-2712): the rail list's pure model — the drag marker, the card's
// meta line and the rail's sort/filter. Kept apart from the component file (react-refresh).
import type { OrderSetApplySummary } from '@/types/order-set-apply-api';
import { plural, setMatchesVisit } from './order-set-apply';

/** The marker a dragged order set carries in the A/P drag payload's `item`. */
export interface OrderSetDragItem {
  __orderSet: true;
  id: string;
  name: string;
}

export function isOrderSetDragItem(item: unknown): item is OrderSetDragItem {
  return !!item && typeof item === 'object' && (item as { __orderSet?: unknown }).__orderSet === true
    && typeof (item as { id?: unknown }).id === 'string';
}

/** "8 orders (2 optional) · E11.9 E78.5 · matches" (board 5). */
export function orderSetMetaLine(set: OrderSetApplySummary, matches: boolean): string {
  const count = plural(set.itemCount, 'order', 'orders');
  const optional = typeof set.optionalCount === 'number' && set.optionalCount > 0 ? ` (${set.optionalCount} optional)` : '';
  const codes = (set.diagnoses ?? []).map((d) => d.code).filter(Boolean).join(' ');
  return [`${count}${optional}`, codes, matches ? 'matches' : ''].filter(Boolean).join(' · ');
}

/**
 * The rail's order: sets matching the visit's diagnoses first (design note s3), each group in the
 * server's own order. Filtered by NAME substring, as the Bundles list was.
 */
export function orderSetsForRail(sets: OrderSetApplySummary[], visitCodes: string[], query: string) {
  const q = query.trim().toLowerCase();
  const rows = sets
    // Only an ACTIVE set is ever offered (PROD-2723 review): the list is fetched with ?status=active,
    // and an archived row that reached it anyway (a stale cache, a backend that ignores the filter)
    // would be refused by the apply.
    .filter((s) => s.status === 'active')
    .filter((s) => !q || s.name.toLowerCase().includes(q))
    .map((set) => ({ set, matches: setMatchesVisit(set.diagnoses ?? [], visitCodes) }));
  return [...rows.filter((r) => r.matches), ...rows.filter((r) => !r.matches)];
}


/** What the rail's Order sets sub-tab needs, minus the query it reads from the rail itself. */
export interface OrderSetsRailWiring {
  visitCodes: string[];
  onApply: (set: OrderSetApplySummary) => void;
  applyingId: string | null;
}

/**
 * The Add-Order drawer's Order sets (PROD-2723). The drawer mounts the SAME rail container as the
 * A/P, so with order sets on its ICD-10 | Bundles toggle reads ICD-10 | Order sets too, and its ＋
 * runs the SAME apply as the A/P rail (`useOrderSetApply().apply`, PROD-2712) with no target
 * diagnosis. Once the apply resolves, `onApplied` closes the drawer and shows the A/P, where the
 * orders, their leftovers and the Undo receipt (or the load error) are.
 */
export function orderSetsForDrawer(
  apply: (setId: string) => unknown,
  applyingId: string | null,
  visitDiagnoses: ReadonlyArray<{ code?: string | null }>,
  onApplied: () => void,
): OrderSetsRailWiring {
  return {
    visitCodes: visitDiagnoses.map((d) => d.code ?? '').filter(Boolean),
    applyingId,
    // Hand over only once the apply has RESOLVED (PROD-2723 review): until then the drawer stays
    // open with every ＋ busy (`applyingId`). The apply never rejects — a set that fails to load
    // leaves its notice on the A/P ("Couldn't load that order set. Try again."), which is where
    // the drawer then lands, so the clinician always sees either the receipt or the error.
    onApply: (set) => {
      void Promise.resolve(apply(set.id)).then(onApplied, onApplied);
    },
  };
}
